/**
 * AI Explainer chat sheet — web port of the iOS feed explainer.
 *
 * Streams from `POST /auth/explain` (SSE) via the shared `getExplain` async
 * generator. Opens as a bottom sheet on phone / centered modal on desktop, shows
 * a streaming assistant reply, lets the user ask follow-ups (threading
 * conversationHistory), and surfaces the remaining-messages quota from the
 * "complete" event.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { X, Sparkles, Send } from "lucide-react";
import { getExplain } from "@/api/Api";
import { tokenStorage } from "@/api/tokenStorage";
import { colors } from "@/ui";
import type { ExplainChatMessage } from "@shared_types";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

interface ExplainerProps {
  /** The Spanish word or phrase being explained. */
  word: string;
  /** Its English translation (best available). */
  translation: string;
  /** The sentence/context the selection came from. */
  context: string;
  isPhrase: boolean;
  /** Lesson the selection came from — included in iOS explainer analytics. */
  lessonId?: number;
  /** Chapter the selection came from — included in iOS explainer analytics. */
  chapterId?: number;
  onClose: () => void;
}

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
}

export function Explainer({
  word,
  translation,
  context,
  isPhrase,
  lessonId,
  chapterId,
  onClose,
}: ExplainerProps) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [remaining, setRemaining] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Running count of user messages sent — iOS message_count payload.
  const messageCountRef = useRef(0);
  const { trackExplainerOpened, trackExplainerMessageSent } =
    useTrackFeedEvents();

  const runStream = useCallback(
    async (history: ExplainChatMessage[]) => {
      const token = tokenStorage.getToken();
      if (!token) {
        setStreaming(false);
        return;
      }
      setStreaming(true);
      // Seed an empty assistant turn we stream into.
      setTurns((prev) => [...prev, { role: "assistant", content: "" }]);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const stream = getExplain({
          authToken: token,
          data: { word, translation, context, conversationHistory: history, isPhrase },
          onEvent: (event) => {
            if (event.type === "complete" && event.remainingMessages != null) {
              setRemaining(event.remainingMessages);
            }
          },
          signal: controller.signal,
        });
        for await (const chunk of stream) {
          setTurns((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === "assistant") {
              next[next.length - 1] = { ...last, content: last.content + chunk };
            }
            return next;
          });
        }
      } catch {
        setTurns((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && !last.content) {
            next[next.length - 1] = {
              ...last,
              content: "Sorry — I couldn't explain that right now. Try again.",
            };
          }
          return next;
        });
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [word, translation, context, isPhrase],
  );

  // Kick off the first explanation on mount.
  useEffect(() => {
    trackExplainerOpened(word, translation, isPhrase, lessonId, chapterId);
    void runStream([]);
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to the latest content as it streams.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns]);

  const send = () => {
    const text = draft.trim();
    if (!text || streaming) return;
    const history: ExplainChatMessage[] = turns.map((t) => ({
      role: t.role,
      content: t.content,
    }));
    setTurns((prev) => [...prev, { role: "user", content: text }]);
    setDraft("");
    messageCountRef.current += 1;
    trackExplainerMessageSent(messageCountRef.current, lessonId, chapterId);
    void runStream([...history, { role: "user", content: text }]);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-[440px] max-h-[80dvh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-[#1e1e1e] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 shrink-0" style={{ color: colors.blue }} />
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">{word}</p>
              <p className="text-white/50 text-xs truncate">{translation}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {turns.map((t, i) => (
            <div
              key={i}
              className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                t.role === "user"
                  ? "self-end bg-white/15 text-white"
                  : "self-start bg-white/5 text-white/90"
              }`}
            >
              {t.content || (streaming && t.role === "assistant" ? "…" : "")}
            </div>
          ))}
        </div>

        {/* Quota */}
        {remaining != null && (
          <p className="px-5 text-white/40 text-xs">{remaining} explanations left today</p>
        )}

        {/* Composer */}
        <div className="flex items-center gap-2 p-3 border-t border-white/10">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") send();
            }}
            placeholder="Ask a follow-up…"
            className="flex-1 bg-white/5 text-white placeholder-white/40 rounded-full px-4 py-2.5 text-sm outline-none focus:bg-white/10"
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim() || streaming}
            aria-label="Send"
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform"
            style={{ backgroundColor: colors.blue }}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
